import {io} from 'socket.io-client';

class SocketService {
    constructor() {
        this.socket = null;
        this.siteRooms = new Map();
        this.reconnectAttempts = 0;
        this.maxReconnectAttempts = 5;
        this.callbacks = {
            'visit:update': [],
            'visit:new': [],
            'site:stats': [],
            'heartbeat-ack': [],
            'event': [],
            'error': []
        };

        this.isInitialized = false;
    }

    // Initialiser la connexion
    initialize() {
        if (this.isInitialized && this.socket) {
           // console.log('SocketService déjà initialisé');
            return;
        }

        // IMPORTANT: Utilisez localhost:3001 pour le développement
        const serverUrl = 'http://localhost:3001';

        //console.log('🔄 Initialisation Socket.IO vers:', serverUrl);

        // CORRECTION: withCredentials: false pour le développement local
        this.socket = io(serverUrl, {
            transports: ['websocket', 'polling'],
            withCredentials: false, // Changé à false pour éviter les problèmes CORS
            reconnection: true,
            reconnectionAttempts: this.maxReconnectAttempts,
            reconnectionDelay: 1000,
            reconnectionDelayMax: 5000,
            timeout: 20000,
            autoConnect: true,
            forceNew: true // Nouvelle connexion à chaque initialisation
        });

        this.setupEventListeners();
        this.isInitialized = true;

        // Démarrer la connexion manuellement si autoConnect est false
        if (!this.socket.connected) {
            this.socket.connect();
        }
    }

    // Configurer les écouteurs d'événements
    setupEventListeners() {
        if (!this.socket) {
          //  console.error('Socket non initialisé');
            return;
        }

        // Événements de base
        this.socket.on('connect', () => {
           // console.log('✅ Socket.IO connecté. ID:', this.socket.id);
            this.reconnectAttempts = 0;

            // Notifier les callbacks de connexion
            this.emitCallbacks('connect', { socketId: this.socket.id });

            // Rejoindre les srooms précédentes
            this.siteRooms.forEach((_, siteKey) => {
               // console.log('Reconnexion à la room:', siteKey);
                this.joinSite(siteKey);
            });
        });

        this.socket.on('connect_error', (error) => {
           // console.error('❌ Erreur de connexion Socket.IO:', error.message);
            this.reconnectAttempts++;

            this.emitCallbacks('error', {
                type: 'connect_error',
                message: error.message
            });

            if (this.reconnectAttempts >= this.maxReconnectAttempts) {
              //  console.warn('⚠️ Nombre maximum de tentatives atteint');
                this.emitCallbacks('error', {
                    type: 'max_reconnect_attempts',
                    message: 'Connexion impossible après plusieurs tentatives'
                });
            }
        });

        this.socket.on('disconnect', (reason) => {
           // console.log('🔌 Socket.IO déconnecté. Raison:', reason);
            this.emitCallbacks('disconnect', { reason });

            if (reason === 'io server disconnect') {
                // Le serveur a déconnecté, tentative de reconnexion
                setTimeout(() => {
                    if (this.socket) {
                        this.socket.connect();
                    }
                }, 1000);
            }
        });

        // Événements métier
        this.socket.on('visit:update', (data) => {
           // console.log('📊 [Socket] Mise à jour visite:', data);
            this.emitCallbacks('visit:update', data);
        });

        this.socket.on('visit:new', (data) => {
           // console.log('🎯 [Socket] Nouvelle visite:', data);
            this.emitCallbacks('visit:new', data);
        });

        this.socket.on('site:stats', (data) => {
           // console.log('📈 [Socket] Stats site:', data);
            this.emitCallbacks('site:stats', data);
        });

        this.socket.on('heartbeat-ack', (data) => {
           // console.log('💓 [Socket] Heartbeat ACK:', data);
            this.emitCallbacks('heartbeat-ack', data);
        });

        this.socket.on('event', (data) => {
          //  console.log('⚡ [Socket] Événement:', data);
            this.emitCallbacks('event', data);
        });

        this.socket.on('error', (error) => {
          //  console.error('❌ [Socket] Erreur:', error);
            this.emitCallbacks('error', error);
        });

        // Pour debug
        this.socket.onAny((event, ...args) => {
           // console.log(`📡 [Socket] Événement ${event}:`, args);
        });
    }

    // Rejoindre un site (room)
    joinSite(siteKey) {
        if (!this.socket) {
           // console.warn('Socket non initialisé');
            this.initialize();
            return Promise.reject(new Error('Socket non initialisé'));
        }

        return new Promise((resolve, reject) => {
            if (!this.socket.connected) {
              //  console.warn('Socket non connecté, attente de connexion...');

                // Attendre la connexion
                const connectHandler = () => {
                    this.socket.off('connect', connectHandler);
                    this.performJoinSite(siteKey, resolve, reject);
                };

                this.socket.on('connect', connectHandler);

                // Timeout
                setTimeout(() => {
                    this.socket.off('connect', connectHandler);
                    reject(new Error('Timeout de connexion'));
                }, 5000);
            } else {
                this.performJoinSite(siteKey, resolve, reject);
            }
        });
    }

    performJoinSite(siteKey, resolve, reject) {
       // console.log(`🔗 Rejoindre le site: ${siteKey}`);

        this.socket.emit('join-site', siteKey, (response) => {
            if (response?.error) {
                console.error('Erreur join-site:', response.error);
                reject(new Error(response.error));
                return;
            }

            this.siteRooms.set(siteKey, true);
           // console.log(`✅ Connecté au site: ${siteKey}`);

            // Récupérer les stats immédiatement
            this.getRealtimeStats(siteKey)
                .then(stats => {
                    resolve({
                        success: true,
                        siteKey,
                        stats,
                        socketId: this.socket.id
                    });
                })
                .catch(error => {
                //    console.warn('Stats non disponibles:', error);
                    resolve({
                        success: true,
                        siteKey,
                        socketId: this.socket.id
                    });
                });
        });
    }

    // Quitter un site
    leaveSite(siteKey) {
        if (this.socket?.connected) {
            this.socket.emit('leave-site', siteKey);
        }
        this.siteRooms.delete(siteKey);
       // console.log(`🔓 Quitté le site: ${siteKey}`);
    }

    // Récupérer les stats temps réel
    getRealtimeStats(siteKey) {
        return new Promise((resolve, reject) => {
            if (!this.socket) {
                reject(new Error('Socket non initialisé'));
                return;
            }

            if (!this.socket.connected) {
                reject(new Error('Socket non connecté'));
                return;
            }

            // Timeout
            const timeout = setTimeout(() => {
                reject(new Error('Timeout récupération stats'));
            }, 10000);

            this.socket.emit('get-realtime-stats', siteKey, (stats) => {
                clearTimeout(timeout);
                if (!stats) {
                    reject(new Error('Aucune donnée reçue'));
                    return;
                }
                resolve(stats);
            });
        });
    }

    // Récupérer l'historique
    getVisitHistory(siteKey, days = 30) {
        return new Promise((resolve, reject) => {
            if (!this.socket) {
                reject(new Error('Socket non initialisé'));
                return;
            }

            if (!this.socket.connected) {
                reject(new Error('Socket non connecté'));
                return;
            }

            // Timeout
            const timeout = setTimeout(() => {
                reject(new Error('Timeout récupération historique'));
            }, 15000);

            this.socket.emit('get-visit-history', siteKey, days, (response) => {
                clearTimeout(timeout);
                if (response?.success) {
                    resolve(response);
                } else {
                    reject(new Error(response?.error || 'Erreur inconnue'));
                }
            });
        });
    }

    // Heartbeat
    sendHeartbeat() {
        if (this.socket?.connected) {
            this.socket.emit('heartbeat');
            return true;
        }
        return false;
    }

    // S'abonner aux événements
    on(event, callback) {
        if (!this.callbacks[event]) {
            this.callbacks[event] = [];
        }
        this.callbacks[event].push(callback);

        // Si le socket existe déjà, écouter l'événement
        if (this.socket && !this.socket.hasListeners(event)) {
            this.socket.on(event, (data) => {
                this.emitCallbacks(event, data);
            });
        }

        return () => this.off(event, callback); // Retourne une fonction de désabonnement
    }

    // Se désabonner
    off(event, callback) {
        if (this.callbacks[event]) {
            this.callbacks[event] = this.callbacks[event].filter(cb => cb !== callback);
        }
    }

    // Émettre les callbacks
    emitCallbacks(event, data) {
        if (this.callbacks[event]) {
            this.callbacks[event].forEach(callback => {
                try {
                    callback(data);
                } catch (error) {
                    console.error(`Erreur dans le callback ${event}:`, error);
                }
            });
        }
    }

    // Déconnexion propre
    disconnect() {
        if (this.socket) {
            // Quitter toutes les rooms
            this.siteRooms.forEach((_, siteKey) => {
                this.leaveSite(siteKey);
            });

            this.socket.disconnect();
            this.socket = null;
            this.siteRooms.clear();
            this.isInitialized = false;
            console.log('🔌 Socket.IO déconnecté proprement');
        }
    }

    // Vérifier l'état
    isConnected() {
        return this.socket?.connected || false;
    }

    // Obtenir l'ID du socket
    getSocketId() {
        return this.socket?.id;
    }

    // Getter pour le socket
    getSocket() {
        return this.socket;
    }
}

// Instance singleton avec initialisation lazy
let instance = null;

export const getSocketService = () => {
    if (!instance) {
        instance = new SocketService();
    }
    return instance;
};

export default getSocketService();
